import axios from "axios"
import yaml from "js-yaml"
import { remark } from "remark"
import { toHast } from "mdast-util-to-hast"
import { selectContent } from "~utils/analytics"

const BASE_URL = "https://raw.githubusercontent.com/box/box-developer-changelog/en"

const astify = (value) => toHast(remark().parse(value))

class Engine {
  constructor({ state, lastShownAt }) {
    const [currentState, setState] = state
    this.state = currentState
    this.setState = setState
    this.lastShownAt = lastShownAt
  }

  async fetchLatest() {
    const result = await axios.get(`${BASE_URL}/index.json`)
    const newItems = result.data.filter((e) => !this.state.entryIds.includes(e))
    newItems.forEach((item) => this.fetch(item))
  }

  async fetch(filename) {
    const result = await axios.get(`${BASE_URL}/${filename}.md`)
    const [_, frontmatter, markdown] = result.data.split("---")
    const title = markdown
      .split("\n")
      .filter((line) => !!line)[0]
      .replace("# ", "")

    this.setState((state) => {
      const newState = { ...state }
      const newEntry = {
        id: filename,
        frontmatter: yaml.load(frontmatter),
        path: `/changelog/#${filename}`,
        htmlAst: astify(markdown),
        excerptAst: astify(markdown.split("<!-- more -->")[0]),
        titleAst: astify(title),
        locale: "en",
        markdown,
        _,
      }
      if (newState.offset > 0) {
        newState.offset += 1
      }
      newState.entries = [newEntry, ...state.entries]
      newState.entryIds = [filename, ...state.entryIds]
      return newState
    })
  }

  /**
   * Returns the current page of entries, filtered by
   * any filters
   */
  page() {
    if (!this.state || !this.state.entries) {
      return []
    }
    const { limit, offset } = this.state

    return this.filteredEntries().slice(offset, offset + limit)
  }

  numberOfPages() {
    return Math.ceil(this.filteredEntries().length / this.state.limit)
  }

  currentPageNumber() {
    return this.state.offset / this.state.limit
  }

  setCurrentPage(index) {
    this.setState((state) => {
      const newState = { ...state }
      newState.offset = state.limit * index
      return newState
    })
    document.getElementById("scroll-body").scrollTo(0, 0)
  }

  filteredEntries() {
    return this.state.entries
      .filter((entry) => {
        if (!this.state.filters.label) {
          return true
        }
        return entry.frontmatter.applies_to.includes(this.state.filters.label)
      })
      .filter((entry) => {
        if (!this.state.filters.is_impactful) {
          return true
        }
        return entry.frontmatter.is_impactful
      })
      .filter((entry) => {
        if (!this.state.filters.is_new_feature) {
          return true
        }
        return entry.frontmatter.is_new_feature
      })
      .filter((entry) => {
        if (!this.state.selected) {
          return true
        }
        return entry.id === this.state.selected.id
      })
  }

  /**
   * Sets a filter value
   */
  filter(key, value) {
    return () => {
      this.setState((state) => {
        const newState = { ...state }
        newState.filters[key] = value
        newState.offset = 0
        return newState
      })
      document.getElementById("scroll-body").scrollTo(0, 0)
    }
  }

  /**
   * Unsets a filter value
   */
  unfilter(key) {
    return () => {
      this.setState((state) => {
        const newState = { ...state }
        newState.filters[key] = null
        newState.offset = 0
        return newState
      })
      document.getElementById("scroll-body").scrollTo(0, 0)
    }
  }

  /**
   * Toggles a filter value
   */
  toggleFilter(key, value) {
    return () => {
      this.setState((state) => {
        const newState = { ...state }
        if (newState.filters[key] === value) {
          newState.filters[key] = null
        } else {
          newState.filters[key] = value
          selectContent(`Changelog - Filter (${key}:${value})`)
        }
        newState.offset = 0
        return newState
      })
      document.getElementById("scroll-body").scrollTo(0, 0)
    }
  }

  /**
   * Checks if a filter value is set
   */
  hasFilter(key, value) {
    if (!this.state) {
      return false
    }
    return this.state.filters[key] === value
  }

  /**
   * Clears all filers
   */
  clearFilters() {
    selectContent("Changelog - Filter (all)")
    this.setState((state) => {
      const newState = { ...state }
      newState.filters.label = null
      newState.filters.is_impactful = false
      newState.filters.is_new_feature = false
      return newState
    })
  }

  /**
   * Checks if the filters are in a default state
   */
  hasDefaultFilters() {
    return (
      this.state.filters.label === null
      && !this.state.filters.is_impactful
      && !this.state.filters.is_new_feature
    )
  }

  /**
   * Returns the ID for an entry
   */
  entryId(entry) {
    return entry.path.split("#")[1]
  }

  /**
   * Collapses the body of all entries
   */
  collapse() {
    this.setState((s) => ({ ...s, ...{ collapsed: true } }))
  }

  filterLabel(microcopy) {
    if (!microcopy) {
      return
    }
    if (this.hasDefaultFilters()) {
      return microcopy.text("changelog.all")
    }
    const labels = []
    if (this.state.filters.label) {
      labels.push(
        microcopy.text(`changelog.labels.${this.state.filters.label}`),
      )
    }
    if (this.state.filters.is_impactful) {
      labels.push(microcopy.text("changelog.levels.is_impactful"))
    }
    if (this.state.filters.is_new_feature) {
      labels.push(microcopy.text("changelog.levels.is_new_feature"))
    }
    return labels.join(", ")
  }

  hasNext() {
    return this.state.limit + this.state.offset < this.filteredEntries().length
  }

  hasPrevious() {
    return this.state.offset > 0
  }

  nextPage() {
    this.setState((state) => {
      const newState = { ...state }
      newState.offset = Math.min(
        state.offset + state.limit,
        this.filteredEntries().length - 1,
      )
      return newState
    })
    document.getElementById("scroll-body").scrollTo(0, 0)
  }

  previousPage() {
    this.setState((state) => {
      const newState = { ...state }
      newState.offset = Math.max(state.offset - state.limit, 0)
      return newState
    })
    document.getElementById("scroll-body").scrollTo(0, 0)
  }

  paginateTo(hash) {
    if (!hash) {
      return
    }
    const id = hash.split("#")[1]
    if (!id) {
      return
    }
    const index = this.state.entryIds.indexOf(id)
    if (index < 0) {
      return
    }

    this.setState((state) => {
      const newState = { ...state }
      newState.offset = index
      return newState
    })
  }

  isNew(entry) {
    if (!this.state.lastShownAt) {
      return false
    }
    return (
      Date.parse(entry.frontmatter.published_at)
      > Date.parse(this.state.lastShownAt)
    )
  }
}

export default Engine
